import type { AxiosResponse } from "axios";
import type { HttpClient } from "@/services/baseClient";
import type {
  ApiResponse,
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  AuthResponse,
  ChangePasswordRequest,
  AuthenticatedChangePasswordRequest,
  InvitationPreviewResponse,
  LoginRequest,
  RegisterRequest,
  UpdateProfileRequest,
  User,
  QuotaUsage,
  UsageEventsResponse,
} from "@/types";

export function createAuthApi({ client }: HttpClient) {
  return {
    async login(credentials: LoginRequest): Promise<AuthResponse> {
      const response: AxiosResponse<ApiResponse<AuthResponse>> =
        await client.post("/auth/login", credentials);
      return response.data.data;
    },

    async register(userData: RegisterRequest): Promise<AuthResponse> {
      const response: AxiosResponse<ApiResponse<AuthResponse>> =
        await client.post("/auth/register", userData);
      return response.data.data;
    },

    async logout(refreshToken: string): Promise<void> {
      await client.post("/auth/logout", { refreshToken });
    },

    async getInvitationByToken(
      token: string,
    ): Promise<InvitationPreviewResponse> {
      const response: AxiosResponse<ApiResponse<InvitationPreviewResponse>> =
        await client.get(
          `/auth/invitations/accept?token=${encodeURIComponent(token)}`,
        );
      return response.data.data;
    },

    async acceptInvitation(
      data: AcceptInvitationRequest,
    ): Promise<AcceptInvitationResponse> {
      const response: AxiosResponse<ApiResponse<AcceptInvitationResponse>> =
        await client.post("/auth/invitations/accept", data);
      return response.data.data;
    },

    async getCurrentUser(): Promise<User> {
      const response: AxiosResponse<ApiResponse<User>> =
        await client.get("/auth/me");
      return response.data.data;
    },

    async changePassword(
      request: ChangePasswordRequest,
    ): Promise<AuthResponse> {
      const response: AxiosResponse<ApiResponse<AuthResponse>> =
        await client.post("/auth/change-password", request);
      return response.data.data;
    },

    async updateLocale(locale: string): Promise<User> {
      const response: AxiosResponse<ApiResponse<User>> = await client.put(
        "/auth/locale",
        { locale },
      );
      return response.data.data;
    },

    async updateProfile(
      userId: string,
      data: UpdateProfileRequest,
    ): Promise<User> {
      const response: AxiosResponse<ApiResponse<User>> = await client.put(
        `/users/${userId}`,
        data,
      );
      return response.data.data;
    },

    async changePasswordAuthenticated(
      data: AuthenticatedChangePasswordRequest
    ): Promise<AuthResponse> {
      const response: AxiosResponse<ApiResponse<AuthResponse>> =
        await client.put("/auth/password", data);
      return response.data.data;
    },

    async generateDeviceLinkCode(): Promise<{
      code: string;
      qrContent: string;
      expiresInSeconds: number;
    }> {
      const response: AxiosResponse<
        ApiResponse<{
          code: string;
          qrContent: string;
          expiresInSeconds: number;
        }>
      > = await client.post("/auth/device-link/generate");
      return response.data.data;
    },

    async approveDeviceLinkChallenge(code: string): Promise<{
      status: "approved";
      code: string;
      deviceId: string;
    }> {
      const response: AxiosResponse<
        ApiResponse<{
          status: "approved";
          code: string;
          deviceId: string;
        }>
      > = await client.post("/auth/device-link/approve", { code });
      return response.data.data;
    },

    async getUserQuota(userId: string): Promise<QuotaUsage> {
      const response: AxiosResponse<ApiResponse<QuotaUsage>> = await client.get(
        `/users/${userId}/quota`,
      );
      return response.data.data;
    },

    async getUserUsageEvents(
      userId: string,
      params: {
        month?: string;
        type?: "all" | "uploaded" | "streamed";
        page?: number;
      } = {},
    ): Promise<UsageEventsResponse> {
      const response: AxiosResponse<ApiResponse<UsageEventsResponse>> =
        await client.get(`/users/${userId}/usage-events`, { params });
      return response.data.data;
    },
  };
}
